import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useQueryClient } from '@zg-rentals/react-query5';
import { Alert, Box, Button, Heading, Paragraph } from '@zillow/constellation-10';
import { IconPlusFilled } from '@zillow/constellation-icons';

import {
  DatarouterError,
  useRentalManagerUserPropertyMultiplexWebApiHandler_create,
  useRentalManagerUserPropertyMultiplexWebApiHandler_getUnitNumbers,
} from '@zg-rentals/rental-manager-env';

import AddPropertyConstants from '../../constants/AddPropertyConstants';
import Bone from '../Bone';
import UnitOverview from './components/UnitOverview';

import type {
  UserPropertyMultiplexCreateUnitRequestWebDto,
  UserPropertyMultiplexCreateUnitResponseWebDto,
  UserPropertyMultiplexCreateUnitsRequestWebDto,
  UserPropertyMultiplexCreateUnitsResponseWebDto,
  UserPropertyMultiplexGetUnitNumbersResponseWebDto,
} from '@zg-rentals/rental-manager-env';

type Props = {
  customerId?: string;
  fullAddress: string;
  numUnits?: string;
  onSuccess?: ({
    unitsCreated,
    hasPartialUnitsSucceeded,
  }: {
    unitsCreated: Array<UserPropertyMultiplexCreateUnitResponseWebDto>;
    hasPartialUnitsSucceeded: boolean;
  }) => void;
  onErrorChange?: ({ hasError, message }: { hasError?: boolean | undefined; message?: string | undefined }) => void;
  onCreateStart?: () => void;
};

export type MultiplexUnitEntryRef = {
  createMultiplex: () => void;
};

const MAX_UNITS = AddPropertyConstants.MAX_NUM_MULTIPLEX_UNITS_VALUE;

const initialData = {
  0: {
    unitNumber: '',
    bedrooms: '',
    baths: '',
  },
  1: {
    unitNumber: '',
    bedrooms: '',
    baths: '',
  },
  2: {
    unitNumber: '',
    bedrooms: '',
    baths: '',
  },
};

const getInitialUnitData = (numUnits?: string) => {
  if (!numUnits) {
    return initialData;
  }

  // ensure the number of units is between 1 and 24
  const count = Math.min(Math.max(Number(numUnits) || 3, 1), MAX_UNITS);
  const data: Record<string, { unitNumber: string; bedrooms: string; baths: string }> = {};

  for (let i = 0; i < count; i++) {
    data[i] = {
      unitNumber: '',
      bedrooms: '',
      baths: '',
    };
  }

  return data;
};

/**
 * forwardRef<T, P>
 *   T - MultiplexUnitEntryRef - The type of the DOM node.
 *   P - Props - The props the component accepts
 */
const MultiplexUnitEntry = forwardRef<MultiplexUnitEntryRef, Props>(
  ({ customerId, fullAddress, numUnits, onErrorChange, onSuccess, onCreateStart }, ref) => {
    const queryClient = useQueryClient();

    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [alertMessage, setAlertMessage] = useState<string | null>(null);
    const [unitOverviewList, setUnitOverviewList] = useState<
      Record<string, { unitNumber: string; bedrooms: string; baths: string }>
    >(getInitialUnitData(numUnits));

    // UnitOverview component level validation errors
    const [errorMap, setErrorMap] = useState<
      Record<
        string,
        {
          hasDuplicateUnitNumber?: boolean | undefined;
          hasEmptyUnitNumber?: boolean | undefined;
          hasMaxCharactersUnitNumber?: boolean | undefined;
        }
      >
    >({});

    const {
      data: unitNumbersResponse = {} as unknown as UserPropertyMultiplexGetUnitNumbersResponseWebDto,
      error: unitNumbersError = {},
      isError: isErrorUnitNumbers,
      isLoading: isLoadingUnitNumbers,
    } = useRentalManagerUserPropertyMultiplexWebApiHandler_getUnitNumbers({
      fullAddress,
    });

    // high level validation errors
    const hasZeroUnitOverviewItems = useMemo(() => Object.keys(unitOverviewList).length === 0, [unitOverviewList]);
    const hasOneUnitOverviewItem = useMemo(() => Object.keys(unitOverviewList).length === 1, [unitOverviewList]);
    const hasReachedMaxUnits = useMemo(() => Object.keys(unitOverviewList).length >= MAX_UNITS, [unitOverviewList]);

    // check to see if ANY unitNumbers are empty in unitOverviewList
    const hasEmptyUnitNumbers = useMemo(
      () => Object.values(unitOverviewList).some((unit) => unit.unitNumber.trim() === ''),
      [unitOverviewList],
    );

    const unitNumbersList = useMemo(() => unitNumbersResponse?.unitNumbers ?? [], [unitNumbersResponse?.unitNumbers]);

    const { mutate: createMultiplexMutation } = useRentalManagerUserPropertyMultiplexWebApiHandler_create({});

    const getHasErrors = useCallback(() => {
      return (
        Object.values(errorMap).some((error) => error.hasDuplicateUnitNumber ?? false) ||
        Object.values(errorMap).some((error) => error.hasEmptyUnitNumber ?? false) ||
        Object.values(errorMap).some((error) => error.hasMaxCharactersUnitNumber ?? false) ||
        hasZeroUnitOverviewItems ||
        hasEmptyUnitNumbers
      );
    }, [errorMap, hasZeroUnitOverviewItems, hasEmptyUnitNumbers]);

    /**
     * expose methods to the parent component
     */
    useImperativeHandle(ref, () => ({
      createMultiplex,
    }));

    useEffect(() => {
      if (isErrorUnitNumbers) {
        const errorMessage = DatarouterError.getDisplayMessage(unitNumbersError);

        setErrorMessage(errorMessage);
        onErrorChange?.({ hasError: true, message: errorMessage });
        return;
      }

      if (unitNumbersResponse) {
        checkForExistingUnits(unitNumbersResponse.unitNumbers ?? []);
      }
    }, [isLoadingUnitNumbers, isErrorUnitNumbers, unitNumbersError, unitNumbersResponse, unitNumbersList]);

    useEffect(() => {
      // callback to the parent component
      onErrorChange?.({ hasError: getHasErrors() });
    }, [errorMap, hasZeroUnitOverviewItems, hasEmptyUnitNumbers]);

    const checkForExistingUnits = (unitNumbers: Array<string>) => {
      if (unitNumbers.length > 0) {
        // Format: "101, 102, and 103"
        const unitListString =
          unitNumbers.length > 1
            ? `${unitNumbers.slice(0, -1).join(', ')} and ${unitNumbers[unitNumbers.length - 1]}`
            : unitNumbers[0];

        setAlertMessage(`Units <strong>${unitListString}</strong> already exist at this property.`);
      }
    };

    const clearError = () => {
      setErrorMessage(null);
      onErrorChange?.({ hasError: false, message: undefined });
    };

    const createMultiplex = () => {
      clearError();
      onCreateStart?.();

      // Convert unitOverviewList Record to Array with proper types
      const units: Array<UserPropertyMultiplexCreateUnitRequestWebDto> = Object.values(unitOverviewList).map(
        (unit) => ({
          unitNumber: unit.unitNumber,
          // if unit.bedrooms = 'studio' then set bedroomCount to 0
          bedroomCount: unit.bedrooms === 'studio' ? 0 : unit.bedrooms ? Number(unit.bedrooms) : undefined,
          bathroomCount: unit.baths ? Number(unit.baths) : undefined,
        }),
      );

      const postData: UserPropertyMultiplexCreateUnitsRequestWebDto = {
        // only include customerId if it is provided
        ...(customerId && { customerId }),
        fullAddress,
        units,
      };

      createMultiplexMutation(
        { postData },
        {
          onSuccess: (response: UserPropertyMultiplexCreateUnitsResponseWebDto) => {
            // invalidate properties query key after create multiplex
            queryClient.invalidateQueries({ queryKey: ['getProperties'], refetchType: 'all' });

            // extract the array of units from the response
            const units = response?.units ?? [];

            // set flags for partial success and all units failed
            const failedUnits = units.filter((unit) => !unit.success);
            const successfulUnits = units.filter((unit) => unit.success);
            const allUnitsFailed = units.every((unit) => !unit.success);
            const hasPartialUnitsSucceeded = successfulUnits.length > 0 && failedUnits.length > 0;

            // if all units failed
            if (allUnitsFailed) {
              // format error messages for failed units
              const errorMessages = failedUnits
                .map((unit) => `Unit ${unit.unitNumber}: ${unit.errorMessage}`)
                .join(' ');

              setErrorMessage(errorMessages);
              onErrorChange?.({ hasError: true });

              return;
            }

            // if partial units succeeded or all units succeeded, proceed to the next step
            clearError();
            onSuccess?.({ unitsCreated: successfulUnits, hasPartialUnitsSucceeded });
          },
          onError: (error) => {
            const errorMessage = DatarouterError.getDisplayMessage(error);

            setErrorMessage(errorMessage);
            onErrorChange?.({ hasError: true, message: errorMessage });
          },
        },
      );
    };

    const handleUnitOverviewChange = (index: string, name: string, value: string) => {
      // Update the unit data
      setUnitOverviewList((prev) => ({
        ...prev,
        [index]: { ...prev[index], [name]: value },
      }));

      // Perform validation for unit number field
      if (name === 'unitNumber') {
        const hasEmptyUnitNumber = value.trim() === '';
        const hasDuplicateUnitNumber = unitNumbersList.includes(value);
        const hasMaxCharactersUnitNumber = value.length > 20;

        setErrorMap((prev) => ({
          ...prev,
          [index]: {
            hasDuplicateUnitNumber,
            hasEmptyUnitNumber,
            hasMaxCharactersUnitNumber,
          },
        }));
      }
    };

    const handleUnitOverviewDelete = (index: string) => {
      setUnitOverviewList((prev) => {
        const updated = { ...prev };
        delete updated[index];

        return updated;
      });

      setErrorMap((prev) => {
        const { [index]: _deleted, ...rest } = prev;

        return rest;
      });
    };

    const handleAddUnit = () => {
      setUnitOverviewList((prev) => {
        // Check if we've already reached the maximum number of units
        if (Object.keys(prev).length >= MAX_UNITS) {
          return prev;
        }

        // add a new unit with a unique incrementing numeric key.
        const keys = Object.keys(prev).map(Number);
        const maxKey = keys.length > 0 ? Math.max(...keys) : -1;
        const newKey = String(maxKey + 1);

        return {
          ...prev,
          [newKey]: {
            unitNumber: '',
            bedrooms: '',
            baths: '',
          },
        };
      });
    };

    const renderSkeletonLoadingState = () => {
      return (
        <>
          {[0, 1, 2].map((index) => (
            <Box
              key={index}
              css={{
                padding: '300',
              }}
            >
              <Box
                css={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '400',
                  justifyContent: 'space-between',
                  alignItems: 'flex-end',
                  marginBottom: '400',
                }}
              >
                {/* Unit Number Field */}
                <Box css={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '200' }}>
                  <Box css={{ width: '80px' }}>
                    <Bone type="line" />
                  </Box>
                  <Box css={{ height: '20px' }}>
                    <Bone type="bar" />
                  </Box>
                  <Box css={{ height: '21.5px' }} />
                </Box>

                {/* Bedrooms Field */}
                <Box css={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '200' }}>
                  <Box css={{ width: '80px' }}>
                    <Bone type="line" />
                  </Box>
                  <Box css={{ height: '20px' }}>
                    <Bone type="bar" />
                  </Box>
                  <Box css={{ height: '21.5px' }} />
                </Box>

                {/* Baths Field */}
                <Box css={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '200' }}>
                  <Box css={{ width: '80px' }}>
                    <Bone type="line" />
                  </Box>
                  <Box css={{ height: '20px' }}>
                    <Bone type="bar" />
                  </Box>
                  <Box css={{ height: '21.5px' }} />
                </Box>

                {/* Delete Button */}
                <Box css={{ width: '20px', height: '20px', marginBottom: '400' }}>
                  <Bone type="cover" />
                </Box>
              </Box>
            </Box>
          ))}
        </>
      );
    };

    return (
      <Box
        data-testid="MultiplexUnitEntry"
        css={{
          paddingLeft: '1200',
          paddingRight: '1200',
          paddingTop: '400',
          paddingBottom: '400',
        }}
      >
        <Box data-testid="TitleAndIntro" css={{ marginBottom: '600' }}>
          <Heading level={4} css={{ marginBottom: '200' }}>
            Tell us about your units
          </Heading>

          <Paragraph textStyle="body">
            Once you add your property, you can list it for free on Zillow, Trulia, and HotPads to help find your
            perfect renter.
          </Paragraph>

          {errorMessage && (
            <Alert data-testid="ErrorAlert" role="status" tone="info" css={{ marginTop: '200' }}>
              {errorMessage}
            </Alert>
          )}

          {alertMessage && (
            <Alert data-testid="AlertMessage" role="status" tone="info" css={{ marginTop: '200' }}>
              <span dangerouslySetInnerHTML={{ __html: alertMessage }} />
            </Alert>
          )}
        </Box>

        <Box
          data-testid="UnitOverviewList-Container"
          css={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
          }}
        >
          <Box data-testid="UnitOverviewList">
            {isLoadingUnitNumbers ? (
              renderSkeletonLoadingState()
            ) : (
              <>
                {Object.keys(unitOverviewList).map((index) => {
                  const unit = unitOverviewList[index];
                  const errors = errorMap[index];

                  return (
                    <UnitOverview
                      key={index}
                      index={Number(index)}
                      unitNumber={unit?.unitNumber ?? ''}
                      bedrooms={unit?.bedrooms ?? ''}
                      baths={unit?.baths ?? ''}
                      hasDuplicateUnitNumber={errors?.hasDuplicateUnitNumber}
                      hasEmptyUnitNumber={errors?.hasEmptyUnitNumber}
                      hasMaxCharactersUnitNumber={errors?.hasMaxCharactersUnitNumber}
                      disableDeleteButton={hasOneUnitOverviewItem}
                      onChange={handleUnitOverviewChange}
                      onDelete={handleUnitOverviewDelete}
                    />
                  );
                })}
              </>
            )}
          </Box>

          <Button
            fluid={true}
            tone="brand"
            emphasis="outlined"
            icon={<IconPlusFilled />}
            onClick={handleAddUnit}
            disabled={hasReachedMaxUnits}
            css={{
              alignSelf: 'center',
            }}
          >
            Add unit
          </Button>
        </Box>
      </Box>
    );
  },
);

MultiplexUnitEntry.displayName = 'MultiplexUnitEntry';

export default MultiplexUnitEntry;
